// Configuration for all Master Data pages. Each entity is defined once here.
// Route: /cadastros/:key -> reads config[key] and renders MasterDataPage

import {
  Building2, Users2, FileSignature, Car, Cog, Factory, PackageOpen, Ruler,
  CreditCard, Landmark, Briefcase, Wrench, Fuel, FileBadge2, GraduationCap, Shirt,
} from 'lucide-react';

const STATUS_OPTIONS = [
  { value: 'active', label: 'Ativo' },
  { value: 'inactive', label: 'Inativo' },
];

export const MASTER_CONFIGS = {
  'categorias-clientes': {
    title: 'Categorias de Clientes', subtitle: 'Categorias para segmentação de clientes',
    endpoint: '/masters/client-categories', icon: Users2, actionLabel: 'Nova Categoria',
    columns: [
      { key: 'name', label: 'Nome' },
      { key: 'color', label: 'Cor', type: 'color' },
      { key: 'description', label: 'Descrição' },
      { key: 'status', label: 'Status', type: 'status' },
    ],
    fields: [
      { key: 'name', label: 'Nome', required: true, colSpan: 2 },
      { key: 'color', label: 'Cor', type: 'color' },
      { key: 'status', label: 'Status', type: 'select', options: STATUS_OPTIONS },
      { key: 'description', label: 'Descrição', type: 'textarea', colSpan: 2 },
    ],
    defaultForm: { name: '', color: '#C52429', description: '', status: 'active' },
  },
  'tipos-contrato': {
    title: 'Tipos de Contrato', subtitle: 'Configurações de tipos de contrato',
    endpoint: '/masters/contract-types', icon: FileSignature, actionLabel: 'Novo Tipo',
    columns: [
      { key: 'name', label: 'Nome' },
      { key: 'generates_financial', label: 'Gera Financeiro', type: 'boolean' },
      { key: 'has_renewal', label: 'Renovação', type: 'boolean' },
      { key: 'status', label: 'Status', type: 'status' },
    ],
    fields: [
      { key: 'name', label: 'Nome', required: true, colSpan: 2 },
      { key: 'generates_financial', label: 'Gera lançamento financeiro', type: 'switch' },
      { key: 'has_renewal', label: 'Possui renovação', type: 'switch' },
      { key: 'status', label: 'Status', type: 'select', options: STATUS_OPTIONS, colSpan: 2 },
      { key: 'description', label: 'Descrição', type: 'textarea', colSpan: 2 },
    ],
    defaultForm: { name: '', description: '', generates_financial: false, has_renewal: false, status: 'active' },
  },
  'marcas-veiculos': {
    title: 'Marcas de Veículos', subtitle: 'Fabricantes de veículos',
    endpoint: '/masters/vehicle-brands', icon: Car, actionLabel: 'Nova Marca',
    columns: [
      { key: 'name', label: 'Nome' },
      { key: 'country', label: 'País' },
      { key: 'status', label: 'Status', type: 'status' },
    ],
    fields: [
      { key: 'name', label: 'Nome', required: true },
      { key: 'country', label: 'País' },
      { key: 'logo_url', label: 'URL do Logo', colSpan: 2 },
      { key: 'status', label: 'Status', type: 'select', options: STATUS_OPTIONS, colSpan: 2 },
    ],
    defaultForm: { name: '', country: '', logo_url: '', status: 'active' },
  },
  'tipos-veiculos': {
    title: 'Tipos de Veículos', subtitle: 'Classificação de veículos',
    endpoint: '/masters/vehicle-types', icon: Cog, actionLabel: 'Novo Tipo',
    columns: [
      { key: 'name', label: 'Nome' }, { key: 'description', label: 'Descrição' },
      { key: 'status', label: 'Status', type: 'status' },
    ],
    fields: [
      { key: 'name', label: 'Nome', required: true },
      { key: 'status', label: 'Status', type: 'select', options: STATUS_OPTIONS },
      { key: 'description', label: 'Descrição', type: 'textarea', colSpan: 2 },
    ],
    defaultForm: { name: '', description: '', status: 'active' },
  },
  'marcas-pneus': {
    title: 'Fabricantes de Pneus', subtitle: 'Marcas de pneus utilizadas',
    endpoint: '/masters/tire-brands', icon: Factory, actionLabel: 'Nova Marca',
    columns: [{ key: 'name', label: 'Nome' }, { key: 'country', label: 'País' }, { key: 'status', label: 'Status', type: 'status' }],
    fields: [
      { key: 'name', label: 'Nome', required: true },
      { key: 'country', label: 'País' },
      { key: 'status', label: 'Status', type: 'select', options: STATUS_OPTIONS, colSpan: 2 },
    ],
    defaultForm: { name: '', country: '', status: 'active' },
  },
  'categorias-produtos': {
    title: 'Categorias de Produtos', subtitle: 'Categorias do estoque',
    endpoint: '/masters/product-categories', icon: PackageOpen, actionLabel: 'Nova Categoria',
    columns: [{ key: 'name', label: 'Nome' }, { key: 'description', label: 'Descrição' }, { key: 'status', label: 'Status', type: 'status' }],
    fields: [
      { key: 'name', label: 'Nome', required: true },
      { key: 'status', label: 'Status', type: 'select', options: STATUS_OPTIONS },
      { key: 'description', label: 'Descrição', type: 'textarea', colSpan: 2 },
    ],
    defaultForm: { name: '', description: '', status: 'active' },
  },
  'unidades-medida': {
    title: 'Unidades de Medida', subtitle: 'UN, KG, L, M, etc.',
    endpoint: '/masters/measurement-units', icon: Ruler, actionLabel: 'Nova Unidade',
    columns: [{ key: 'code', label: 'Código', mono: true }, { key: 'name', label: 'Nome' }, { key: 'description', label: 'Descrição' }, { key: 'status', label: 'Status', type: 'status' }],
    fields: [
      { key: 'code', label: 'Código', required: true, mono: true },
      { key: 'name', label: 'Nome', required: true },
      { key: 'status', label: 'Status', type: 'select', options: STATUS_OPTIONS, colSpan: 2 },
      { key: 'description', label: 'Descrição', type: 'textarea', colSpan: 2 },
    ],
    defaultForm: { code: '', name: '', description: '', status: 'active' },
  },
  'formas-pagamento': {
    title: 'Formas de Pagamento', subtitle: 'Métodos de pagamento aceitos',
    endpoint: '/masters/payment-methods', icon: CreditCard, actionLabel: 'Nova Forma',
    columns: [{ key: 'name', label: 'Nome' }, { key: 'days_to_pay', label: 'Prazo (dias)' }, { key: 'status', label: 'Status', type: 'status' }],
    fields: [
      { key: 'name', label: 'Nome', required: true },
      { key: 'days_to_pay', label: 'Prazo (dias)', type: 'number' },
      { key: 'status', label: 'Status', type: 'select', options: STATUS_OPTIONS, colSpan: 2 },
      { key: 'description', label: 'Descrição', type: 'textarea', colSpan: 2 },
    ],
    defaultForm: { name: '', description: '', days_to_pay: 0, status: 'active' },
  },
  'bancos': {
    title: 'Bancos', subtitle: 'Instituições financeiras',
    endpoint: '/masters/banks', icon: Landmark, actionLabel: 'Novo Banco',
    columns: [{ key: 'code', label: 'Código', mono: true }, { key: 'name', label: 'Nome' }, { key: 'ispb', label: 'ISPB', mono: true }, { key: 'status', label: 'Status', type: 'status' }],
    fields: [
      { key: 'code', label: 'Código', required: true, mono: true },
      { key: 'ispb', label: 'ISPB', mono: true },
      { key: 'name', label: 'Nome', required: true, colSpan: 2 },
      { key: 'status', label: 'Status', type: 'select', options: STATUS_OPTIONS, colSpan: 2 },
    ],
    defaultForm: { code: '', name: '', ispb: '', status: 'active' },
  },
  'cargos': {
    title: 'Cargos', subtitle: 'Cargos e funções',
    endpoint: '/masters/job-positions', icon: Briefcase, actionLabel: 'Novo Cargo',
    columns: [{ key: 'name', label: 'Nome' }, { key: 'department_name', label: 'Departamento' }, { key: 'status', label: 'Status', type: 'status' }],
    fields: [
      { key: 'name', label: 'Nome', required: true, colSpan: 2 },
      { key: 'status', label: 'Status', type: 'select', options: STATUS_OPTIONS, colSpan: 2 },
      { key: 'description', label: 'Descrição', type: 'textarea', colSpan: 2 },
    ],
    defaultForm: { name: '', description: '', status: 'active' },
  },
  'departamentos': {
    title: 'Departamentos', subtitle: 'Departamentos organizacionais',
    endpoint: '/masters/departments', icon: Building2, actionLabel: 'Novo Departamento',
    columns: [{ key: 'name', label: 'Nome' }, { key: 'description', label: 'Descrição' }, { key: 'status', label: 'Status', type: 'status' }],
    fields: [
      { key: 'name', label: 'Nome', required: true },
      { key: 'status', label: 'Status', type: 'select', options: STATUS_OPTIONS },
      { key: 'description', label: 'Descrição', type: 'textarea', colSpan: 2 },
    ],
    defaultForm: { name: '', description: '', status: 'active' },
  },
  'tipos-manutencao': {
    title: 'Tipos de Manutenção', subtitle: 'Classificações de manutenção',
    endpoint: '/masters/maintenance-types', icon: Wrench, actionLabel: 'Novo Tipo',
    columns: [{ key: 'name', label: 'Nome' }, { key: 'description', label: 'Descrição' }, { key: 'status', label: 'Status', type: 'status' }],
    fields: [
      { key: 'name', label: 'Nome', required: true },
      { key: 'status', label: 'Status', type: 'select', options: STATUS_OPTIONS },
      { key: 'description', label: 'Descrição', type: 'textarea', colSpan: 2 },
    ],
    defaultForm: { name: '', description: '', status: 'active' },
  },
  'tipos-combustivel': {
    title: 'Tipos de Combustível', subtitle: 'Diesel, Gasolina, etc.',
    endpoint: '/masters/fuel-types', icon: Fuel, actionLabel: 'Novo Tipo',
    columns: [{ key: 'name', label: 'Nome' }, { key: 'description', label: 'Descrição' }, { key: 'status', label: 'Status', type: 'status' }],
    fields: [
      { key: 'name', label: 'Nome', required: true },
      { key: 'status', label: 'Status', type: 'select', options: STATUS_OPTIONS },
      { key: 'description', label: 'Descrição', type: 'textarea', colSpan: 2 },
    ],
    defaultForm: { name: '', description: '', status: 'active' },
  },
  'tipos-documento': {
    title: 'Tipos de Documento', subtitle: 'CNH, CRLV, Contratos, etc.',
    endpoint: '/masters/document-types', icon: FileBadge2, actionLabel: 'Novo Tipo',
    columns: [{ key: 'name', label: 'Nome' }, { key: 'description', label: 'Descrição' }, { key: 'status', label: 'Status', type: 'status' }],
    fields: [
      { key: 'name', label: 'Nome', required: true },
      { key: 'status', label: 'Status', type: 'select', options: STATUS_OPTIONS },
      { key: 'description', label: 'Descrição', type: 'textarea', colSpan: 2 },
    ],
    defaultForm: { name: '', description: '', status: 'active' },
  },
  'treinamentos': {
    title: 'Treinamentos', subtitle: 'Catálogo de treinamentos',
    endpoint: '/masters/trainings', icon: GraduationCap, actionLabel: 'Novo Treinamento',
    columns: [
      { key: 'name', label: 'Nome' },
      { key: 'validity_days', label: 'Validade (dias)' },
      { key: 'mandatory', label: 'Obrigatório', type: 'boolean' },
      { key: 'periodicity_months', label: 'Periodicidade (meses)' },
      { key: 'status', label: 'Status', type: 'status' },
    ],
    fields: [
      { key: 'name', label: 'Nome', required: true, colSpan: 2 },
      { key: 'validity_days', label: 'Validade (dias)', type: 'number' },
      { key: 'periodicity_months', label: 'Periodicidade (meses)', type: 'number' },
      { key: 'mandatory', label: 'Obrigatório', type: 'switch' },
      { key: 'status', label: 'Status', type: 'select', options: STATUS_OPTIONS },
      { key: 'description', label: 'Descrição', type: 'textarea', colSpan: 2 },
    ],
    defaultForm: { name: '', description: '', validity_days: null, mandatory: false, periodicity_months: null, status: 'active' },
  },
  'uniformes': {
    title: 'Uniformes', subtitle: 'Uniformes e EPIs',
    endpoint: '/masters/uniforms', icon: Shirt, actionLabel: 'Novo Uniforme',
    columns: [
      { key: 'name', label: 'Nome' },
      { key: 'code', label: 'Código', mono: true },
      { key: 'size', label: 'Tamanho' },
      { key: 'category', label: 'Categoria' },
      { key: 'useful_life_months', label: 'Vida útil (meses)' },
      { key: 'status', label: 'Status', type: 'status' },
    ],
    fields: [
      { key: 'name', label: 'Nome', required: true, colSpan: 2 },
      { key: 'code', label: 'Código', mono: true },
      { key: 'size', label: 'Tamanho' },
      { key: 'category', label: 'Categoria' },
      { key: 'useful_life_months', label: 'Vida útil (meses)', type: 'number' },
      { key: 'status', label: 'Status', type: 'select', options: STATUS_OPTIONS, colSpan: 2 },
    ],
    defaultForm: { name: '', code: '', size: '', category: '', useful_life_months: null, status: 'active' },
  },
  'empresas-proprietarias': {
    title: 'Empresas Proprietárias', subtitle: 'Empresas e CNPJs proprietários dos veículos',
    endpoint: '/masters/owner-companies', icon: Building2, actionLabel: 'Nova Empresa',
    columns: [
      { key: 'name', label: 'Razão Social / Nome' },
      { key: 'cnpj', label: 'CNPJ', mono: true },
      { key: 'internal_id', label: 'Sigla / Código', mono: true },
      { key: 'status', label: 'Status', type: 'status' },
    ],
    fields: [
      { key: 'name', label: 'Razão Social / Nome', required: true, colSpan: 2 },
      { key: 'cnpj', label: 'CNPJ', required: true, mono: true },
      { key: 'internal_id', label: 'Sigla / Código', mono: true },
      { key: 'status', label: 'Status', type: 'select', options: STATUS_OPTIONS, colSpan: 2 },
    ],
    defaultForm: { name: '', cnpj: '', internal_id: '', status: 'active' },
  },
};

export const MASTER_KEYS = Object.keys(MASTER_CONFIGS);
